import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Wand2, ArrowRight, Check, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { intensityClass, fmtDate } from "../lib/workout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import PlannedView from "./PlannedView";

const EXAMPLES = [
  "Make Thursday easier — I have a work trip",
  "I want a harder weekend long run",
  "Move my rest day to Friday",
];

function ChangeCard({ c }) {
  const p = c.proposed || {};
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid={`proposal-change-${c.workout_id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{fmtDate(c.date)}</p>
        {p.intensity && <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${intensityClass(p.intensity)}`}>{p.intensity}</span>}
      </div>
      <div className="flex items-center gap-2 text-sm mb-1">
        <span className="text-muted-foreground line-through truncate">{c.current?.title}</span>
        <ArrowRight size={14} className="text-brand-orange shrink-0" />
        <span className="font-semibold text-foreground truncate">{p.title}</span>
      </div>
      {c.reason && <p className="text-xs text-muted-foreground mb-3">{c.reason}</p>}
      {!p.is_rest && p.segments?.length > 0 && (
        <div className="rounded-lg bg-background/60 p-3"><PlannedView segments={p.segments} /></div>
      )}
    </div>
  );
}

export default function PlanEditDialog({ open, onOpenChange, onApplied }) {
  const [instruction, setInstruction] = useState("");
  const [proposal, setProposal] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);

  const reset = () => { setInstruction(""); setProposal(null); setProposalId(null); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const propose = async () => {
    if (!instruction.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/plan/propose", { instruction, proposal_id: proposalId });
      setProposal(data.proposal);
      setProposalId(data.proposal_id);
      setInstruction("");
    } catch (e) {
      toast("Couldn't reach Caden", { description: formatApiErrorDetail(e.response?.data?.detail) || "Try again" });
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/plan/apply", { proposal_id: proposalId });
      toast("Plan updated", { description: `${data.applied.length} session${data.applied.length === 1 ? "" : "s"} changed` });
      onApplied?.();
      close(false);
    } catch (e) {
      toast("Couldn't apply", { description: formatApiErrorDetail(e.response?.data?.detail) || "Try again" });
    } finally { setApplying(false); }
  };

  const changes = proposal?.changes || [];
  const needsClar = proposal?.clarification_needed;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="plan-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold text-2xl tracking-tight flex items-center gap-2">
            <Wand2 size={22} className="text-brand-orange" /> Edit with Caden
          </DialogTitle>
          <DialogDescription>Tell Caden how to change your upcoming plan. He'll propose it — you approve or refine.</DialogDescription>
        </DialogHeader>

        {proposal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            <div className="rounded-xl bg-muted/60 p-4 flex items-start gap-3" data-testid="proposal-message">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${needsClar ? "bg-brand-yellow/20" : "brand-gradient"}`}>
                {needsClar ? <HelpCircle className="text-brand-yellow" size={16} /> : <Sparkles className="text-white" size={15} />}
              </div>
              <p className="text-foreground leading-relaxed text-[15px]">{proposal.message}</p>
            </div>
            {changes.map((c) => <ChangeCard key={c.workout_id} c={c} />)}
            {changes.length > 0 && (
              <Button data-testid="proposal-apply" onClick={apply} disabled={applying} className="w-full h-12 rounded-xl font-semibold active:scale-95 transition-transform">
                <Check size={18} className="mr-1.5" /> {applying ? "Applying…" : `Apply ${changes.length} change${changes.length === 1 ? "" : "s"}`}
              </Button>
            )}
          </motion.div>
        )}

        <div className="space-y-2 pt-1">
          {!proposal && (
            <div className="flex flex-wrap gap-2" data-testid="plan-edit-examples">
              {EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => setInstruction(ex)} className="text-xs px-3 py-1.5 rounded-full border border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors">
                  {ex}
                </button>
              ))}
            </div>
          )}
          <Textarea data-testid="plan-edit-input" value={instruction} onChange={(e) => setInstruction(e.target.value)}
            placeholder={proposal ? "Refine it further… (e.g. 'keep Saturday as is')" : "e.g. I'm travelling Wed–Fri, make those light and shift the key session"} rows={2} />
          <Button data-testid="plan-edit-submit" onClick={propose} disabled={busy || !instruction.trim()} variant={proposal ? "outline" : "default"} className="w-full h-11 rounded-xl font-semibold">
            {busy ? "Caden is planning…" : proposal ? "Refine" : "Ask Caden"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
